import { fetcher } from '@commenda/utils';
import { useQuery } from '@tanstack/react-query';
import { Country, CorporationType, TaxChartClass } from '@prisma/client';
import { businessEntityKeys } from './keys';

export type OrgStructureKeyPerson = {
  directorId: number;
  name: string;
};

export type OrgStructureBankAccount = {
  id: string;
  name: string | null;
};

export type OrgStructureEntity = {
  id: number;
  legalName: string;
  incorporationCountry: Country | null;
  businessEntityType: CorporationType | null;
  keyPersons: { data: OrgStructureKeyPerson[]; count: number };
  bankAccounts: { data: OrgStructureBankAccount[]; count: number };
  taxClassification: string | null;
  // Coarse class driving the tax-chart shape; resolved BE-side.
  taxChartClass: TaxChartClass;
  // Raw Tier-3 override, or null when auto-resolved — gates the Reset action.
  taxChartClassOverride: TaxChartClass | null;
  isNonControlledEntity: boolean;
};

export type OrgStructureEdge = {
  fromCorporationId: number;
  toCorporationId: number;
  // Raw % within the share class (human-readable, e.g. 32.5).
  ownershipPercent: number;
  // % of the target corp's total authorized shares. Falls back to
  // `ownershipPercent` server-side when the issuer has incomplete share
  // data. The chart edge label should prefer this field.
  dilutedOwnershipPercent: number;
  // Null for legacy stakeholdings without an explicit class.
  stockClassId: string | null;
  // Control-only links (percent 0 + this true) render as dashed and unlabeled.
  hasSignificantControl: boolean;
};

export type OrgStructureStockClass = {
  id: string;
  // The corp that issued the class.
  corporationId: number;
  name: string;
  authorizedShares: number | null;
};

export type OrgStructure = {
  entities: OrgStructureEntity[];
  edges: OrgStructureEdge[];
  // Flat across the workspace; consumers group by `corporationId` for
  // per-class detail rendering.
  stockClasses: OrgStructureStockClass[];
  // Legacy: edges arrive pre-unscaled now. Kept on the wire for any
  // bundle still in the wild that divides by it.
  percentScale: number;
};

export const getCompanyOrgStructure = async (companyId: number): Promise<OrgStructure> => {
  const response = await fetcher(
    `business-entities/org-structure?companyId=${companyId}`,
    { method: 'GET' },
  );
  if (!response.ok) throw new Error('Failed to fetch company org structure');
  return (await response.json()) as OrgStructure;
};

export const useCompanyOrgStructure = (companyId: number, options?: { enabled?: boolean }) => {
  return useQuery({
    queryFn: () => getCompanyOrgStructure(companyId),
    queryKey: [businessEntityKeys.FETCH_COMPANY_ORG_STRUCTURE, companyId],
    staleTime: 0,
    refetchOnMount: true,
    // The endpoint is behind `OwnershipFeatureFlagGuard` server-side; pass
    // `enabled: false` from the consumer when the flag is off so non-cohort
    // users don't fire a request that will 404.
    enabled: options?.enabled ?? true,
  });
};
